import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { SolanaUtils } from '../utils/solana.utils';
import { Id, toast } from 'react-toastify';
import { MessageSignatureModel, UserProviderValue } from '../models';
import { authenticate, getUserAuthToken, linkWeb3Wallet, requestEmailVerification } from '../services';
import { UserContext } from '../contexts/user.context';
import { useSearchParams } from 'react-router-dom';
import { SolanaMobileWalletAdapterWalletName } from '@solana-mobile/wallet-standard-mobile';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { WalletName } from '@solana/wallet-adapter-base';
import { SOLANA_SIGNATURE_MESSAGES } from '../constants';
import { SignupStatuses } from '../enums';

export const useLoginWeb3 = () => {
   const { isAuthenticated, setIsAuthenticated, setUser, setIsLoginModalOpen, user } = useContext<UserProviderValue>(UserContext);
   const [searchParams] = useSearchParams();
   const referralCode = searchParams.get('referral');

   const [authRequested, setAuthRequested] = useState(false);
   const [isLedger, setIsLedger] = useState(false);
   const toastId = useRef<Id>(-1);
   const [loading, setLoading] = useState(false);
   const [verificationStatus, setVerificationStatus] = useState<SignupStatuses>(SignupStatuses.Initial);
   const [pendingSignature, setPendingSignature] = useState<MessageSignatureModel | null>(null);

   const [ledgerExpanded, setLedgerExpanded] = useState(false);
   const wallet = useWallet();
   const modalWallet = useWalletModal();

   const showWalletConfirmModal = useMemo(() => {
      return wallet?.publicKey && authRequested;
   }, [wallet, authRequested]);

   useEffect(() => {
      if (!user || authRequested) return;
      if (!authRequested && wallet?.publicKey && !user.wallets?.find((userWallet) => userWallet.publicKey === wallet?.publicKey?.toString() && userWallet.isActive)) {
         setAuthRequested(true);
      }
   }, [wallet, user, isAuthenticated, authRequested]);

   // Helper function to show loading toast
   const showLoadingToast = (message: string) => {
      toastId.current = toast.loading(message, {
         position: 'bottom-left',
         autoClose: false,
         hideProgressBar: false,
         closeOnClick: false,
         pauseOnHover: false,
         draggable: false,
      });
   };

   // Helper function to update toast
   const updateToast = (type: 'success' | 'error' | 'info', message: string, autoClose: number = 10000) => {
      if (toastId.current !== -1) {
         toast.update(toastId.current, {
            type: toast.TYPE[type.toUpperCase() as keyof typeof toast.TYPE],
            render: message,
            autoClose,
            closeOnClick: true,
            isLoading: false,
         });
         // toast.done(toastId.current);
      }
   };

   // Helper function to reset authentication state
   const resetAuthState = () => {
      setAuthRequested(false);
      setVerificationStatus(SignupStatuses.Initial);
      setPendingSignature(null);
      setIsLedger(false);
      setLedgerExpanded(false);
   };

   // Helper function to handle successful authentication
   const handleAuthSuccess = (userData: any, message: string = 'Authenticated successfully') => {
      if (!isAuthenticated) setIsAuthenticated(true);
      resetAuthState();
      setIsLoginModalOpen(false);
      setUser(userData);
      updateToast('success', message, 1000);
      setLoading(false);
   };

   // Check if 2FA is required for wallet linking
   const requiresTwoFactorAuth = (): boolean => {
      return isAuthenticated === true && !!user?.email && !user?.wallets?.find((userWallet) => userWallet.publicKey === wallet?.publicKey?.toString() && userWallet.isActive);
   };

   // Handle 2FA email verification request
   const handleTwoFactorAuthFlow = async (signature: MessageSignatureModel) => {
      if (!user?.email || !wallet?.publicKey) {
         throw new Error('Missing required information for 2FA');
      }

      setPendingSignature(signature);

      const verificationResponse = await requestEmailVerification(user.email, wallet.publicKey.toString());

      if (verificationResponse.error) {
         updateToast('error', verificationResponse.error.message || 'Failed to send verification email');
         setIsLoginModalOpen(false);
         resetAuthState();
         setLoading(false);
         throw new Error(verificationResponse.error.message || 'Failed to send verification email');
      }

      setVerificationStatus(SignupStatuses.VerificationEmailSent);
      updateToast('info', 'Verification code sent to your email', 3000);
      setLoading(false);
   };

   const authenticateUser = async () => {
      if (!wallet?.publicKey) return;

      try {
         setLoading(true);
         showLoadingToast('Authenticating...');

         const authToken = await getUserAuthToken();
         if (!authToken?.data) {
            updateToast('error', 'Auth service encountered an error, please try again.');
            setLoading(false);
            return;
         }

         const signature: MessageSignatureModel = await SolanaUtils.signMessage(
            wallet,
            isLedger ? SOLANA_SIGNATURE_MESSAGES.AUTH.MESSAGE_SHORT(authToken.data.token) : SOLANA_SIGNATURE_MESSAGES.AUTH.MESSAGE_LONG(authToken.data.token),
            authToken.data,
            isLedger
         );
         signature.referralCode = referralCode ?? undefined;

         // If linking wallet and user has email, require 2FA
         if (requiresTwoFactorAuth()) {
            await handleTwoFactorAuthFlow(signature);
            return;
         }

         // Direct authentication or wallet linking without 2FA
         const result = await (isAuthenticated ? linkWeb3Wallet(signature) : authenticate(signature));

         if (result?.error) {
            throw new Error(result.error.message || 'Failed to authenticate');
         }

         if (result?.data) {
            handleAuthSuccess(result.data);
            return;
         }

         throw new Error('Failed to authenticate');
      } catch (e: any) {
         console.error('Authentication error:', e);
         updateToast('error', e?.message || "The signature couldn't be verified, please try again.");
         resetAuthState();
         setIsLoginModalOpen(false);
         if (!isAuthenticated) setIsAuthenticated(false);
      } finally {
         setLoading(false);
      }
   };

   const submitVerificationCode = async (verificationCode: string) => {
      if (!pendingSignature || !user?.email) {
         toast.error('An error occurred. Please try again');
         resetAuthState();
         return;
      }

      if (!verificationCode || verificationCode.length !== 6) {
         toast.error('Please enter a valid 6-digit verification code');
         return;
      }

      try {
         setLoading(true);
         showLoadingToast('Linking wallet...');

         const signatureWithCode: MessageSignatureModel = {
            ...pendingSignature,
            emailVerificationCode: verificationCode.trim(),
         };

         const result = await linkWeb3Wallet(signatureWithCode);

         if (result?.error) {
            throw new Error(result.error.message || 'Failed to link wallet');
         }

         if (result?.data) {
            handleAuthSuccess(result.data, 'Wallet linked successfully');
            return;
         }

         throw new Error('Failed to link wallet');
      } catch (e: any) {
         console.error('Wallet linking error:', e);
         updateToast('error', e?.message || 'Failed to link wallet. Please try again.');
         setLoading(false);
      }
   };

   const resendVerificationCode = async () => {
      if (!user?.email || !wallet?.publicKey) {
         toast.error('An error occurred. Please try again');
         return;
      }

      try {
         setLoading(true);
         const verificationResponse = await requestEmailVerification(user.email, wallet.publicKey.toString());

         if (verificationResponse.error) {
            toast.error(verificationResponse.error.message || 'Failed to send verification email');
            setLoading(false);
            return;
         }

         toast.success('Verification code sent to your email');
         setLoading(false);
      } catch (e: any) {
         console.error('Resend verification error:', e);
         toast.error('Failed to send verification email');
         setLoading(false);
      }
   };

   const cancelVerification = () => {
      resetAuthState();
   };

   const handleConnect = async () => {
      if (!wallet) return;
      // MWA is only available if user is on Android Web environments (e.g Android Chrome).
      if (wallet?.wallet?.adapter?.name === SolanaMobileWalletAdapterWalletName) {
         // If already selected, immediately connect.
         await wallet.connect();
      } else if (wallet?.wallets?.find((wallet) => wallet.adapter.name === SolanaMobileWalletAdapterWalletName)) {
         // If MWA is not selected, but available, select it instead of showing modal.

         // modalWallet?.setVisible(false);
         wallet?.disconnect();

         wallet?.select(SolanaMobileWalletAdapterWalletName as unknown as WalletName);
      } else {
         wallet?.disconnect();
         // Else, show modal as usual.
         modalWallet?.setVisible(true);
      }
   };

   const disconnectWallet = () => {
      wallet?.disconnect();
      setAuthRequested(false);
   };

   return {
      isAuthenticated,
      isLedger,
      ledgerExpanded,
      loading,
      wallet,
      setIsLedger,
      setLedgerExpanded,
      authenticateUser,
      disconnectWallet,
      showWalletConfirmModal,
      modalWallet,
      handleConnect,
      verificationStatus,
      submitVerificationCode,
      resendVerificationCode,
      cancelVerification,
      user,
   };
};
